import React, { useContext, useState } from 'react';

import { ArrowRightIcon, CloseIcon } from '$partials/icon';
import { ModalContext } from '$contexts/modal.jsx';
import { Button, Filter, Select } from '$partials/ui';
import { formatNumber, formatPrice } from '$utilities/format';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/pages.d.ts').PageFilters}
 */
const FilterModal = ({
    priceFilterOptions,
    bedroomFilterOptions,
    internalAreaFeetFilterOptions,
    amenityFilterOptions,
    amenityFilters,
    setAmenityFilters,
    areaFilterOptions,
    areaFilters,
    setAreaFilters,
    periodFilterOptions,
    periodFilters,
    setPeriodFilters,
    furnishedStatus,
    setMinPrice,
    maxPrice,
    minArea,
    minBedroom,
    minPrice,
    setMaxPrice,
    setMinArea,
    setMinBedroom,
    setActiveFilter,
    tenancyFilterOptions,
    setTenureFilters = () => {},
    tenureFilters
}) => {
    const { handleModal } = useContext(ModalContext);
    const [ localState, setLocalState ] = useState({
        amenities: amenityFilters,
        area: areaFilters,
        period: periodFilters,
        maxPrice: maxPrice,
        minPrice: minPrice,
        minArea: minArea,
        minBedroom: minBedroom,
        tenure: tenureFilters
    });

    return (
        <div className='fixed left-0 top-0 height-100 width-100 border-0 m0 p0 overflow-auto bg-white z3 flex flex-wrap'>
            <div
                className='absolute top-0 width-100 z5 pt5 px5 flex justify-between items-center'>
                <p className='type-style-5'>Filters</p>
                <Button
                    aria-label='Close'
                    as = 'button'
                    className='black'
                    look = 'button'
                    onClick={() => {
                        handleModal();
                    }}
                    renderIcon = {<CloseIcon/>}/>
            </div>
            <div className='col-12 px5 pt13 flex flex-wrap height-0'>
                <div className='flex flex-wrap width-100'>
                    <div className='col-12 flex flex-wrap'>
                        <div className='col-12 sm-col-6 md-col-3 relative border black'>
                            <Select
                                label='Min Price'
                                onChange={(value) => {
                                    setLocalState({...localState, minPrice: value});
                                }}
                                options={[{label:'No min', value:''}].concat(priceFilterOptions?.map(option=> {return {label: formatPrice(option), value: option};}))}
                                value={localState.minPrice}/>
                        </div>
                        <div className='col-12 sm-col-6 md-col-3 relative border-left border-right border-bottom sm-border-0 sm-border-right sm-border-top sm-border-bottom black'>
                            <Select
                                label='Max Price'
                                onChange={(value) => {
                                    setLocalState({...localState, maxPrice: value});
                                }}
                                options={priceFilterOptions?.map(option=> {return {label: formatPrice(option), value: option};}).concat([{label:'No max', value:''}])}
                                value={localState.maxPrice} />
                        </div>
                        <div className='col-12 sm-col-6 md-col-3 relative border-bottom border-left border-right md-border-0 md-border-right md-border-top md-border-bottom black'>
                            <Select
                                label='Min Bedrooms'
                                onChange={(value) => {
                                    setLocalState({...localState, minBedroom: value});
                                }}
                                options={[{label:'No min', value:''}].concat(bedroomFilterOptions?.map(option=> {return {label: option, value: option};}))}
                                value={localState.minBedroom} />
                        </div>
                        <div className='col-12 sm-col-6 md-col-3 relative border-left border-right border-bottom sm-border-0 sm-border-bottom sm-border-right md-border-0 md-border-right md-border-top md-border-bottom black'>
                            <Select
                                label='Min sq. feet'
                                onChange={(value) => {
                                    setLocalState({...localState, minArea: value});
                                }}
                                options={[{label:'No min', value:''}].concat(internalAreaFeetFilterOptions?.map(option=> {return {label: formatNumber(option), value: option};}))}
                                value={localState.minArea} />
                        </div>
                    </div>
                    <div className='col-12 flex flex-wrap black'>
                        <div className='col-12 md-col-4 p6 border-bottom border-right border-left'>
                            <p className='type-style-5'>Tenancy</p>
                            <div className='pt4'>
                                {tenancyFilterOptions?.map((tenancy) => {
                                    return <Filter
                                        className='mb-2px mr-2px'
                                        key={`property-listing-page-${tenancy.value}`}
                                        onClick={() => {
                                            const index = localState.tenure.findIndex(queryTenancy => queryTenancy === tenancy.value);
                                            if(index === -1) {
                                                setLocalState({ ...localState,  tenure: [...localState.tenure, tenancy.value]});
                                            } else {
                                                setLocalState({...localState, tenure: localState.tenure.filter((val, idx) => idx !== index)});
                                            }
                                        }}
                                        selected={localState.tenure.findIndex(queryTenancy => queryTenancy === tenancy.value) !== -1}
                                        title={tenancy.label}/>;
                                })}
                            </div>
                        </div>
                        <div className='col-12 md-col-4 p6 border-bottom border-right border-left'>
                            <p className='type-style-5'>Amenities</p>
                            <div className='pt4'>
                                {amenityFilterOptions?.map((amenity) => {
                                    return <Filter
                                        className='mb-2px mr-2px'
                                        key={`property-listing-page-${amenity.id}`}
                                        onClick={() => {
                                            const index = localState.amenities.findIndex(queryAmenity => queryAmenity === amenity.value);
                                            if(index === -1) {
                                                setLocalState({ ...localState,  amenities: [...localState.amenities, amenity.value]});
                                            } else {
                                                setLocalState({...localState, amenities: localState.amenities.filter((val, idx) => idx !== index)});
                                            }
                                        }}
                                        selected={localState.amenities.findIndex(queryAmenity => queryAmenity === amenity.value) !== -1}
                                        title={amenity.label}/>;
                                })}
                            </div>
                        </div>
                        <div className='col-12 md-col-4 p6 border-left border-right border-bottom md-border-0 md-border-bottom md-border-right '>
                            <p className='type-style-5'>Area</p>
                            <div className='pt4'>
                                {areaFilterOptions?.map((area) => {
                                    return <Filter
                                        className='mb-2px mr-2px'
                                        key={`property-listing-page-${area.id}`}
                                        onClick={() => {
                                            const index = localState.area.findIndex(queryArea => queryArea === area.value);
                                            if(index === -1) {
                                                setLocalState({ ...localState,  area: [...localState.area, area.value]});
                                            } else {
                                                setLocalState({...localState, area: localState.area.filter((val, idx) => idx !== index)});
                                            }
                                        }}
                                        selected={localState.area.findIndex(queryArea => queryArea === area.value) !== -1}
                                        title={area.label}/>;
                                })
                                }
                            </div>
                        </div>
                        <div className='col-12 md-col-4 p6 border-left border-right border-bottom md-border-0 md-border-bottom md-border-right'>
                            <p className='type-style-5'>Period</p>
                            <div className='pt4'>
                                {periodFilterOptions?.map((period) => {
                                    return <Filter
                                        className='mb-2px mr-2px'
                                        key={`property-listing-page-${period.id}`}
                                        onClick={() => {
                                            const index = localState.period.findIndex(queryPeriod => queryPeriod === period.value);
                                            if(index === -1) {
                                                setLocalState({ ...localState,  period: [...localState.period, period.value]});
                                            } else {
                                                setLocalState({...localState, period: localState.period.filter((val, idx) => idx !== index)});
                                            }
                                        }}
                                        selected={localState.period.findIndex(queryPeriod => queryPeriod === period.value) !== -1}
                                        title={period.label}/>;
                                })
                                }
                            </div>
                        </div>
                    </div>
                    <div className='col-12 flex justify-end mt6 sm-mt0'>
                        <Button
                            as = 'button'
                            className = 'bg-black white type-style-5 sm-max-width-350px'
                            look = 'fill'
                            onClick={() => {
                                setMinPrice(localState.minPrice);
                                setMaxPrice(localState.maxPrice);
                                setMinArea(localState.minArea);
                                setMinBedroom(localState.minBedroom);
                                setAmenityFilters(localState.amenities);
                                setTenureFilters(localState.tenure);
                                setAreaFilters(localState.area);
                                setPeriodFilters(localState.period);
                                handleModal();
                                setActiveFilter('');
                            }}
                            renderIcon = {<ArrowRightIcon/>}>
                            View Results
                        </Button>
                    </div>

                </div>
            </div>
        </div>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default FilterModal ;
